import {
	ActionIcon,
	createStyles,
	Group,
	Input,
	Loader,
	Stack,
} from '@mantine/core';
import { useDebouncedValue, useDidUpdate, useMediaQuery } from '@mantine/hooks';
import { useSuggestions } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import type { FilterView } from '@repo/common/components/Filter/types';
import { Pagination } from '@repo/common/components/Pagination';
import { Button, Icon, Text, Title } from '@repo/foundations';
import { size } from 'lodash-es';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router';
import { useAuthUser, useSearch, useTrackSearchLog } from '../../api';
import { DOCUMENTATION_WIDTH } from '../../components/Documentation/constants';
import { FilterViewModal } from '../../components/Filter/Views/FilterViewModal';
import { FilterViews } from '../../components/Filter/Views/FilterViews';
import { FilterBar } from '../../components/GlobalSearch/FilterBar';
import { useGlobalSearch } from '../../components/GlobalSearch/useGlobalSearch';
import { closeAllModals, openModal } from '../../components/ModalManager';
import { useAiWorkspaceSettingEnabled } from '../../hooks/useAiWorkspaceSettingEnabled';
import { useUserOnboarding } from '../../hooks/useUserOnboarding';
import {
	deleteParamsFromUrl,
	getParamsFromUrl,
	setParamsInUrl,
} from '../../utils/url';
import SearchResults from './SearchResults/SearchResults';

const useStyles = createStyles((theme) => ({
	topBar: {
		padding: `${theme.spacing.xs} 0`,
		minWidth: theme.other.space[60], // 240px
		width: '100%',
		alignSelf: 'center',
		justifyContent: 'space-between',
		flex: 0,

		[theme.fn.smallerThan('lg')]: {
			display: 'none',
		},
	},
	breadcrumbWrapper: {},
	actionsWrapper: {},
	container: {
		width: '100%',
		overflowY: 'auto',
		flex: 1,

		[theme.fn.smallerThan('xl')]: {
			marginTop: theme.spacing.xs,
		},
	},
	innerContainer: {
		maxWidth: DOCUMENTATION_WIDTH, // 640px
		width: '100%',
		gap: 0,
		alignSelf: 'center',
	},
	input: {
		borderRadius: theme.radius.md,
		boxShadow: theme.shadows.sm,
	},
	iconGroup: {
		justifyContent: 'flex-end',
		transitionProperty: 'width',
		transitionTimingFunction: 'ease-in',
		transitionDuration: '250ms',
	},
	header: {
		maxWidth: DOCUMENTATION_WIDTH, // 640px
		minWidth: theme.other.space[60], // 240px
		width: '100%',
		alignSelf: 'center',
		gap: 0,
		marginTop: -theme.other.space[9], // .topBar height

		[theme.fn.smallerThan('xl')]: {
			marginTop: theme.spacing.xs,
		},
	},
}));

function SearchPage() {
	const { classes, theme } = useStyles();
	const isSmallScreen = useMediaQuery(
		`(max-width: ${theme.breakpoints.xl})`,
		false
	);

	const globalSearchStore = useGlobalSearch();

	const { dismissViewerOnboardingStepSearch } = useUserOnboarding();

	const { user, workspace } = useAuthUser();
	const enableAi = useAiWorkspaceSettingEnabled();
	const navigate = useNavigate();

	const [page, setPage] = useState(1);

	const [debouncedSearchTerm] = useDebouncedValue(
		globalSearchStore.searchTerm,
		350
	);

	const { mutate: trackSearchLog } = useTrackSearchLog();

	const onSearchComplete = useCallback(() => {
		dismissViewerOnboardingStepSearch();
		if (!!debouncedSearchTerm?.length) {
			trackSearchLog({
				term: debouncedSearchTerm,
				source: 'search_page',
			});
		}
	}, [dismissViewerOnboardingStepSearch, debouncedSearchTerm, trackSearchLog]);

	const { isFetching, data } = useSearch({
		page,
		searchTerm: debouncedSearchTerm,
		filterV2: globalSearchStore.catalogFilter,
		sortV2: globalSearchStore.catalogSort,
		onSearchComplete,
	});
	// debouncing so the spinners won't show if the search response is fast
	const [debouncedIsFetching] = useDebouncedValue(isFetching, 300);

	const { data: suggestion } = useSuggestions({
		queryParams: {
			term: debouncedSearchTerm,
		},
	});

	const setSearchTermFromSuggestion = () => {
		globalSearchStore.setSearchTerm(suggestion?.suggestion || '');
	};

	const showSuggestion =
		suggestion?.suggestion && suggestion?.suggestion !== debouncedSearchTerm;

	useEffect(() => {
		// loads the search term from the url on mount
		globalSearchStore.setSearchTerm(getParamsFromUrl().get('term') || '');
	}, [globalSearchStore]);

	useDidUpdate(() => {
		// sync the search term from the store to the url
		if (debouncedSearchTerm) {
			setParamsInUrl('term', debouncedSearchTerm);
		} else {
			deleteParamsFromUrl('term');
		}
	}, [debouncedSearchTerm]);

	useEffect(() => {
		// reset page when search term or filters changes
		setPage(1);
	}, [debouncedSearchTerm, globalSearchStore.catalogFilter]);

	const handleReset = useCallback(() => {
		globalSearchStore.reset();
	}, [globalSearchStore]);

	const handleFilterViewModalClose = useCallback(
		(newView?: FilterView | null) => {
			if (newView) {
				globalSearchStore.setFilterView(newView);
			}
			closeAllModals();
		},
		[globalSearchStore]
	);

	const handleOpenFilterViewModal = useCallback(
		(view?: FilterView | null) => {
			openModal({
				title: view ? 'Edit view' : 'Save view',
				children: (
					<FilterViewModal
						filterOptions={globalSearchStore.filterOptions}
						onClose={handleFilterViewModalClose}
						view={view ?? null}
						selectedFilters={toJS(globalSearchStore.values)}
					/>
				),
			});
		},
		[globalSearchStore, handleFilterViewModalClose]
	);

	const handleNavigateToAi = useCallback(() => {
		trackEvent('search/ai', {}, user, workspace);
		navigate('/ai/new');
	}, [navigate, user, workspace]);

	const searchIcon = debouncedIsFetching ? (
		<Loader size={16} />
	) : (
		<Icon name="search" />
	);

	const handleCancelViewChanges = useCallback(() => {
		globalSearchStore.setValues(globalSearchStore.viewValues);
	}, [globalSearchStore]);

	const renderFilterViewTarget = useCallback(
		(view: FilterView | undefined, isMenuOpen: boolean, toggle: () => void) => (
			<Button
				variant="tertiary"
				onClick={toggle}
				data-testid="search-view-button"
			>
				<Title weight={'semibold'} size="sm">
					{view ? view.label : 'All results'}
				</Title>
				<Icon color="icon/primary/default" name={'selector'} iconPadding={0} />
			</Button>
		),
		[]
	);

	return (
		<Stack spacing={0} px="sm" sx={{ height: '100vh', overflow: 'hidden' }}>
			{globalSearchStore.searchTerm && (
				<Helmet>
					<title>Results for {globalSearchStore.searchTerm}</title>
				</Helmet>
			)}
			{!isSmallScreen && (
				<Group className={classes.topBar}>
					<Group>
						<FilterViews
							handleEdit={handleOpenFilterViewModal}
							onChange={globalSearchStore.setFilterView}
							value={globalSearchStore.view}
							renderTarget={renderFilterViewTarget}
						/>
					</Group>
					<Group position="right" className={classes.actionsWrapper}>
						{globalSearchStore.valuesDiffersFromViewValues && (
							<Group spacing="xs" className={classes.iconGroup}>
								<Button
									size="md"
									variant="tertiary"
									onClick={handleCancelViewChanges}
								>
									Cancel
								</Button>
								<Button
									size="md"
									onClick={() =>
										handleOpenFilterViewModal(globalSearchStore.view)
									}
								>
									Save view
								</Button>
							</Group>
						)}
					</Group>
				</Group>
			)}
			<Stack className={classes.header}>
				<Input
					classNames={{ input: classes.input }}
					icon={searchIcon}
					variant="default"
					placeholder={
						globalSearchStore.view
							? `Search in ${globalSearchStore.view.label}`
							: 'Search company data'
					}
					rightSection={
						enableAi && (
							<ActionIcon variant="subtle" onClick={handleNavigateToAi}>
								<Icon name="sparkles" color="fill/ai/default" />
							</ActionIcon>
						)
					}
					value={globalSearchStore.searchTerm}
					onChange={(e) => globalSearchStore.setSearchTerm(e.target.value)}
					data-testid="search-page-search-input"
					autoFocus
				/>
				<FilterBar
					showTopLevelOperator
					showFilterViews={isSmallScreen}
					onOpenFilterViewModal={
						globalSearchStore.values.length > 0
							? handleOpenFilterViewModal
							: undefined
					}
				/>
				{showSuggestion && (
					<Group position="left" mb="md" spacing="3xs">
						<Text size="sm">Did you mean: </Text>
						<Button onClick={setSearchTermFromSuggestion} variant="tertiary">
							<Text size="sm" weight="semibold" color="text/emphasis/default">
								{suggestion?.suggestion}
							</Text>
						</Button>
					</Group>
				)}
			</Stack>
			<Stack className={classes.container}>
				<Stack className={classes.innerContainer}>
					{!isFetching && (
						<SearchResults
							page={page}
							pageSize={20}
							isFetching={debouncedIsFetching}
							results={data?.results || []}
							term={debouncedSearchTerm}
							onReset={handleReset}
						/>
					)}
					{!isFetching && size(data?.results) > 0 && (
						<Pagination
							page={page}
							total={data?.total_pages || 1}
							onChange={setPage}
							hidden={debouncedIsFetching}
						/>
					)}
				</Stack>
			</Stack>
		</Stack>
	);
}

// eslint-disable-next-line react-refresh/only-export-components
const SearchPageWithObserver = observer(SearchPage);
export default SearchPageWithObserver;
